import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableCellNode } from '@lexical/table';
import {
  $getSelection,
  $isParagraphNode,
  $nodesOfType,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useEffect, useRef } from 'react';
import {
  $containingSuggestionNode,
  $focusSuggestion,
  $getSuggestionState,
  $setMarkersHidden,
  $setSuggestionFullCell,
  SUGGESTION_COLORS,
  SuggestionNode,
} from '../nodes/SuggestionNode';

type Props = {
  showMarkers?: boolean;
};

export function SuggestionPlugin({ showMarkers = true }: Props) {
  const [editor] = useLexicalComposerContext();
  useSuggestionCellStyle(editor);
  useSuggestionFocusStyle(editor, showMarkers);
  useMarkersVisibility(editor, showMarkers);
  return null;
}

function useSuggestionCellStyle(editor: LexicalEditor) {
  useEffect(() => {
    const unregisterTransform = editor.registerNodeTransform(SuggestionNode, node => {
      const parent = node.getParent();
      const grandParent = parent?.getParent();

      const parentIsParagraph = $isParagraphNode(parent);
      const parentIsTableCell = $isTableCellNode(parent);
      const grandParentIsTableCell = $isTableCellNode(grandParent);

      const isInTableCell = (parentIsParagraph && grandParentIsTableCell) || parentIsTableCell;

      const containerChildren = parent?.getChildren() || [];

      const isOnlyContent = containerChildren.every(
        child => child === node || child.getTextContent().trim().length === 0,
      );

      const suggestionIsOnlyCellContent = isInTableCell && isOnlyContent;

      const currentState = $getSuggestionState(node);

      if (currentState.isFullCell !== suggestionIsOnlyCellContent) {
        $setSuggestionFullCell(node, suggestionIsOnlyCellContent);
      }
    });

    const unregisterUpdate = editor.registerUpdateListener(() => {
      const rootElement = editor.getRootElement();
      if (!rootElement) return;

      rootElement.querySelectorAll<HTMLElement>('[data-suggestion-cell]').forEach(cell => {
        cell.style.backgroundColor = '';
        cell.style.transition = '';
        delete cell.dataset.suggestionCell;
      });

      editor.getEditorState().read(() => {
        for (const node of $nodesOfType(SuggestionNode)) {
          const state = $getSuggestionState(node);
          if (!state.isFullCell || state.markersHidden) continue;

          const element = editor.getElementByKey(node.getKey());
          if (!element) continue;

          const cell = element.closest<HTMLElement>(
            '.editor-table-cell, .editor-table-cell-header',
          );
          if (!cell) continue;

          const bgColor =
            SUGGESTION_COLORS[state.source][state.focusedAt != null ? 'focused' : 'unfocused'];
          cell.style.backgroundColor = bgColor;
          cell.style.transition = 'background-color 0.4s ease-in-out';
          cell.dataset.suggestionCell = 'true';
        }
      });
    });

    return () => {
      unregisterTransform();
      unregisterUpdate();
    };
  }, [editor]);
}

function useSuggestionFocusStyle(editor: LexicalEditor, showMarkers: boolean) {
  const showMarkersRef = useRef(showMarkers);

  useEffect(() => {
    showMarkersRef.current = showMarkers;
  }, [showMarkers]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        if (!showMarkersRef.current) return false;

        const selection = $getSelection();
        selection?.getNodes().forEach(node => {
          const suggestionNode = $containingSuggestionNode(node);
          if (suggestionNode) {
            $focusSuggestion(suggestionNode);
          }
        });
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);
}

function useMarkersVisibility(editor: LexicalEditor, showMarkers: boolean) {
  const showMarkersRef = useRef(showMarkers);
  const prevShowMarkersRef = useRef(showMarkers);

  useEffect(() => {
    showMarkersRef.current = showMarkers;

    if (prevShowMarkersRef.current !== showMarkers) {
      prevShowMarkersRef.current = showMarkers;

      // Prevent the caret being scrolled into view after toggling markers
      editor.update(() => {
        $setSelection(null);
      });
    }

    editor.update(() => {
      $nodesOfType(SuggestionNode).forEach(node => {
        $setMarkersHidden(node, !showMarkers);
      });
    });
  }, [editor, showMarkers]);

  useEffect(() => {
    return editor.registerNodeTransform(SuggestionNode, node => {
      const shouldBeHidden = !showMarkersRef.current;
      if ($getSuggestionState(node).markersHidden !== shouldBeHidden) {
        $setMarkersHidden(node, shouldBeHidden);
      }
    });
  }, [editor]);
}
